import { remarkEmojiShortcodes } from "@/components/ai-assistant/ai-assistant-remark-emoji.ts";
import { buildAiAssistantTableCsv } from "@/components/ai-assistant/ai-assistant-table-csv.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { Button, FontAwesomeIcon, Stack, Table } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import { ComponentProps, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import Markdown, { Components, ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

const remarkPlugins = [remarkGfm, remarkEmojiShortcodes];

const COLLAPSED_ROW_COUNT = 5;

type HastElement = NonNullable<ExtraProps["node"]>;
type HastContent = HastElement["children"][number];

interface AiAssistantTableCell {
  text: string;
  content: ReactNode;
}

function AiAssistantLink({ href, title, children }: ComponentProps<"a">) {
  return (
    <a href={href} title={title} target={"_blank"} rel={"noopener noreferrer"}>
      {children}
    </a>
  );
}

const components: Components = {
  a: AiAssistantLink,
  table: AiAssistantTable,
};

// В скрытом регионе для скринридера кнопка «Скачать CSV» дала бы лишнюю остановку Tab.
const announcementComponents: Components = {
  ...components,
  table: ({ children }) => <table>{children}</table>,
};

interface AiAssistantMarkdownProps {
  children: string;
  isAnnouncement?: boolean;
}

export function AiAssistantMarkdown({
  children,
  isAnnouncement = false,
}: AiAssistantMarkdownProps) {
  return (
    <Markdown
      remarkPlugins={remarkPlugins}
      components={isAnnouncement ? announcementComponents : components}
    >
      {children}
    </Markdown>
  );
}

const childElements = (node: HastElement) =>
  node.children.filter((x): x is HastElement => x.type === "element");

const toText = (node: HastContent): string =>
  node.type === "text"
    ? node.value
    : node.type === "element"
      ? node.children.map(toText).join("")
      : "";

// Строка GFM-таблицы всегда шириной в заголовок: короткие дополняются пустыми ячейками.
const readTableRows = (table: HastElement): AiAssistantTableCell[][] =>
  childElements(table)
    .flatMap(childElements)
    .map((row) =>
      childElements(row).map((cell) => ({
        text: cell.children.map(toText).join(""),
        content: toJsxRuntime(
          { type: "root", children: cell.children },
          { Fragment, jsx, jsxs, components: { a: AiAssistantLink } }
        ),
      }))
    );

function AiAssistantTable({ node }: ExtraProps) {
  const { t } = useTranslation();
  const { createDownloadLink } = useDownloadHelper();
  const [isExpanded, setIsExpanded] = useState(false);
  const [header = [], ...rows] = node ? readTableRows(node) : [];

  const columns: ColumnDef<AiAssistantTableCell[]>[] = header.map((cell, index) => ({
    id: String(index),
    header: cell.text,
    cell: ({ row }) => row.original[index].content,
  }));

  const downloadCsv = () => {
    createDownloadLink(
      buildAiAssistantTableCsv([header, ...rows].map((row) => row.map((cell) => cell.text))),
      "assistant-table.csv"
    );
  };

  return (
    <div className={"my-5 min-w-0 first:mt-0 last:mb-0"}>
      <div
        className={
          "border-l-2 border-dark/20 pl-4 [&_tbody_tr]:h-auto [&_td]:py-3 [&_td]:text-dark [&_th]:py-3 [&_tbody_tr:last-child_td]:border-b-0 [&_tfoot]:hidden"
        }
      >
        <Table data={isExpanded ? rows : rows.slice(0, COLLAPSED_ROW_COUNT)} columns={columns} />
      </div>
      <Stack direction={"horizontal"} gap={"sm"} className={"mt-5"}>
        {!isExpanded && rows.length > COLLAPSED_ROW_COUNT && (
          <Button size={"sm"} variant={"secondary"} onClick={() => setIsExpanded(true)}>
            {t("ai:assistant_show_all_rows", { count: rows.length })}
          </Button>
        )}
        <Button
          size={"sm"}
          variant={"secondary"}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-file-csv"} />}
          onClick={downloadCsv}
        >
          {t("ai:assistant_download_csv")}
        </Button>
      </Stack>
    </div>
  );
}
